import { Component, LLMProvider, Message, DashCompRequestMessageSchema, T_RESPONSE, CollectionRegistry, Tool, DashCompModelConfig } from "../types";
import { logger } from "../utils/logger";
import { llmUsageLogger } from "../utils/llm-usage-logger";
import { ZodError } from "zod";

import { DashCompReqType, DashCompResponse } from "./types";
import { sendDashCompResponse } from "./utils";
import { pickComponentWithLLM } from "./pick-component";
import { createFilterWithLLM } from "./create-filter";
import { dashboardConversationHistory } from "./dashboard-conversation-history";

// Re-export types for external use
export * from "./types";
export { dashboardConversationHistory } from "./dashboard-conversation-history";

/**
 * Process DASH_COMP_REQ request
 */
export const processDashCompRequest = async (
	data: any,
	components: Component[],
	_sendMessage: (message: Message) => void,
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	collections?: CollectionRegistry,
	tools?: Tool[],
	dashCompModels?: DashCompModelConfig
): Promise<DashCompResponse> => {
	const errors: string[] = [];

	// Parse incoming message data
	logger.debug('[DASH_COMP_REQ] Parsing incoming message data');
	const parseResult = DashCompRequestMessageSchema.safeParse(data);

	if (!parseResult.success) {
		const zodError = parseResult.error as ZodError;
		zodError.errors.forEach(err => {
			errors.push(`${err.path.join('.')}: ${err.message}`);
		});
		return { success: false, errors };
	}

	const dashCompRequest = parseResult.data;
	const { id, payload } = dashCompRequest;
	const prompt = payload.prompt;
	const reqType: DashCompReqType = payload.req_type || 'create';
	const existingComponents = payload.existingComponents || [];
	const wsId = dashCompRequest.from.id || 'unknown';
	const dashboardId = payload.dashboardId;
	const userId = payload.userId;

	// Get conversation history scoped to this user + dashboard
	const conversationHistory = dashboardId
		? dashboardConversationHistory.getHistory(dashboardId, userId)
		: undefined;

	// Reset log files for this request
	const promptContext = `DASH_COMP[${reqType}]: ${prompt?.substring(0, 50)}${(prompt?.length || 0) > 50 ? '...' : ''}`;
	llmUsageLogger.resetLogFile(promptContext);

	// Validate required fields
	if (!prompt) {
		errors.push('Prompt is required');
	}

	// Validate filter request has existing components
	if (reqType === 'filter' && existingComponents.length === 0) {
		errors.push('Filter request requires existingComponents');
	}

	if (errors.length > 0) {
		return { success: false, errors, id, wsId };
	}

	logger.info(`[DASH_COMP_REQ] Processing ${reqType} request for prompt: "${prompt.substring(0, 50)}..."`);
	logger.info(`[DASH_COMP_REQ] Available: ${components?.length || 0} components, ${tools?.length || 0} tools`);
	if (existingComponents.length > 0) {
		logger.info(`[DASH_COMP_REQ] Existing components in dashboard: ${existingComponents.length}`);
	}

	// Check if components are available
	if (!components || components.length === 0) {
		logger.warn('[DASH_COMP_REQ] No components available');
		return {
			success: false,
			errors: ['No components available. Please ensure components are loaded.'],
			id,
			wsId
		};
	}

	let llmResponse: T_RESPONSE;

	// Route based on request type
	if (reqType === 'filter') {
		// Filter request: create filter + update existing components
		llmResponse = await createFilterWithLLM(
			prompt,
			components,
			existingComponents,
			anthropicApiKey,
			groqApiKey,
			geminiApiKey,
			openaiApiKey,
			llmProviders,
			tools,
			dashCompModels,
			collections,
			userId
		);
	} else {
		// Create or Update request: use dash-comp-picker
		llmResponse = await pickComponentWithLLM(
			prompt,
			components,
			anthropicApiKey,
			groqApiKey,
			geminiApiKey,
			openaiApiKey,
			llmProviders,
			collections,
			tools,
			dashCompModels,
			conversationHistory,
			userId
		);
	}

	// Store conversation entry for this user's dashboard on success
	if (llmResponse.success && dashboardId && prompt) {
		const comp = llmResponse.data?.component;
		let componentSummary = 'No component generated';
		if (comp) {
			const parts: string[] = [];
			if (comp.type) parts.push(`Component Type: ${comp.type}`);
			if (comp.name) parts.push(`Name: ${comp.name}`);
			if (comp.description) parts.push(`Description: ${comp.description}`);
			componentSummary = parts.join('\n') || 'Component generated';
		}
		dashboardConversationHistory.addEntry(dashboardId, prompt, componentSummary, userId);
	}

	// Log session summary
	llmUsageLogger.logSessionSummary(`DASH_COMP[${reqType}]: ${prompt?.substring(0, 30)}`);

	return {
		success: llmResponse.success,
		data: llmResponse.data,
		errors: llmResponse.errors,
		id,
		wsId
	};
};

/**
 * Handle DASH_COMP_REQ WebSocket message
 */
export async function handleDashCompRequest(
	data: any,
	components: Component[],
	sendMessage: (message: Message) => void,
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	collections?: CollectionRegistry,
	tools?: Tool[],
	dashCompModels?: DashCompModelConfig
): Promise<void> {
	const response = await processDashCompRequest(
		data,
		components,
		sendMessage,
		anthropicApiKey,
		groqApiKey,
		geminiApiKey,
		openaiApiKey,
		llmProviders,
		collections,
		tools,
		dashCompModels
	);

	// Send response back via WebSocket
	sendDashCompResponse(
		response.id || data.id,
		{
			success: response.success,
			errors: response.errors,
			data: response.data
		},
		sendMessage,
		response.wsId || data.from?.id
	);

	logger.info(`[DASH_COMP_REQ] Response sent to client ${response.wsId || data.from?.id}`);
}
